import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import { useEffect, useState } from "react";
import axios from "axios";
import { hostname, parseError } from "../../utils";

interface LeaderboardEntry {
  userUid: string;
  totalCredits: number;
  requestCount: number;
  email?: string;
  displayName?: string;
  photoURL?: string;
}

interface LeaderboardData {
  leaderboard: LeaderboardEntry[];
}

export default function UsageLeaderboard({
  companyId,
  getToken,
  eventListener,
}: {
  companyId: string;
  getToken: Function;
  eventListener: Function;
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<LeaderboardData | null>(null);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/usage/leaderboard?companyId=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response.data);
    } catch (error) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchLeaderboard();
    }
  }, [companyId]);

  const getAvatarText = (entry: LeaderboardEntry) => {
    if (entry.displayName) {
      const names = entry.displayName.trim().split(' ');
      if (names.length >= 2) {
        return names[0][0] + names[names.length - 1][0];
      }
      return names[0][0];
    }
    return entry.email ? entry.email[0].toUpperCase() : '?';
  };

  const getUserDisplayName = (entry: LeaderboardEntry) => {
    return entry.displayName || entry.email || 'Anonymous User';
  };

  const formatCredits = (credits: number) => {
    if (credits >= 1000000) {
      return `${(credits / 1000000).toFixed(1)}M`;
    } else if (credits >= 1000) {
      return `${(credits / 1000).toFixed(1)}K`;
    }
    return credits.toFixed(0);
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return `#${rank}`;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader
          avatar={<LeaderboardIcon />}
          title="Usage Leaderboard"
          subheader="Loading top users..."
        />
        <CardContent>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.leaderboard.length === 0) {
    return (
      <Card>
        <CardHeader
          avatar={<LeaderboardIcon />}
          title="Usage Leaderboard"
          subheader="Top 10 users by credit usage"
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary" align="center">
            No usage data available yet
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        avatar={<LeaderboardIcon />}
        title="Usage Leaderboard"
        subheader="Top 10 users in this company (last 30 days)"
      />
      <CardContent sx={{ p: 0 }}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }}>Rank</TableCell>
                <TableCell>User</TableCell>
                <TableCell align="right">Credits Used</TableCell>
                <TableCell align="right">Requests</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.leaderboard.map((entry, index) => (
                <TableRow
                  key={entry.userUid}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    backgroundColor: index < 3 ? 'action.hover' : 'inherit',
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: index < 3 ? 'bold' : 'normal',
                        fontSize: index === 0 ? '1.2rem' : '1rem',
                      }}
                    >
                      {getRankBadge(index + 1)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={entry.photoURL}
                        alt={getUserDisplayName(entry)}
                        sx={{ width: 32, height: 32 }}
                      >
                        {getAvatarText(entry)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {getUserDisplayName(entry)}
                        </Typography>
                        {entry.displayName && entry.email && (
                          <Typography variant="caption" color="text.secondary">
                            {entry.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatCredits(entry.totalCredits)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {entry.requestCount.toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}